import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PageHero, useQuery, FilterGroup, Check, toggleIn, Empty } from '../../shared/components/Common';
import Icon from '../../shared/components/Icons';
import { addDaysIso, fmtDay, formatPrice, placeOf } from '../../shared/data/common';
import { airlines, generateFlights } from './data';
import { AirlineLogo } from './FlightSummary';
import { useSupplierSearch } from '../../api/search';
import { offerDetail } from '../../api/SupplierResults';
import { saveSelection, withFare } from './liveSelection';

const LIVE = process.env.NODE_ENV !== 'test';

const timeSlots = [
  ['early', 'Early', 'Before 6 AM', 0, 6], ['morning', 'Morning', '6 AM – 12 PM', 6, 12],
  ['afternoon', 'Afternoon', '12 PM – 6 PM', 12, 18], ['night', 'Night', 'After 6 PM', 18, 24],
];

const stopOptions = [[0, 'Non-stop'], [1, '1 stop'], [2, '2+ stops']];

const sortOptions = [
  ['cheap', 'Cheapest'], ['fast', 'Fastest'], ['early', 'Earliest'], ['late', 'Latest'],
];

const fallbackColors = ['#3867d6', '#e63946', '#0a8d81', '#8854d0', '#f4a261', '#2d3436'];

const toMins = (hhmm) => { const [h, m] = hhmm.split(':').map(Number); return h * 60 + m; };
const durationMins = (text) => {
  const h = /(\d+)\s*h/.exec(text || '');
  const m = /(\d+)\s*m/.exec(text || '');
  return (h ? Number(h[1]) * 60 : 0) + (m ? Number(m[1]) : 0);
};
const formatDuration = (mins) => `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, '0')}m`;
const slotOf = (mins) => timeSlots.find(([, , , a, b]) => mins >= a * 60 && mins < b * 60)[0];
const stopBucket = (stops) => Math.min(stops, 2);

function clockTime(value) {
  const match = value.match(/(?:T|\s)(\d{2}:\d{2})/);
  return match ? match[1] : value;
}

function parseJson(value, fallback) {
  try { return value ? JSON.parse(value) : fallback; } catch { return fallback; }
}

function airlineFor(code, name) {
  const known = airlines.find((a) => a.code === code);
  if (known) return { ...known, name: name || known.name };
  const color = fallbackColors[[...(code || '')].reduce((n, c) => n + c.charCodeAt(0), 0) % fallbackColors.length];
  return { code: code || 'FL', name: name || code || 'Airline', color };
}

const fareLabels = { OFFER_FARE_WITH_PNR: 'Offer fare', OFFER_FARE_WITHOUT_PNR: 'Offer fare' };

/** Readable fare-family name: PUBLISHED → Published, NDC_Value → NDC Value. */
function fareLabel(id) {
  if (!id) return 'Standard';
  if (fareLabels[id]) return fareLabels[id];
  return id.replace(/_/g, ' ').toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\b(Sme|Ndc|Gds)\b/g, (w) => w.toUpperCase());
}

/** All fare families the supplier offers for this flight, cheapest first. */
function faresFor(offer) {
  const options = parseJson(offerDetail(offer, 'fareOptions'), []);
  const list = options.length ? options : [{
    offerReference: offerDetail(offer, 'offerReference'),
    fareIdentifier: offerDetail(offer, 'fareIdentifier'),
    price: offer.totalPrice,
    refundType: offerDetail(offer, 'refundType'),
    cabinBaggage: offerDetail(offer, 'cabinBaggage'),
    checkinBaggage: offerDetail(offer, 'checkinBaggage'),
    mealIncluded: offerDetail(offer, 'mealIncluded') === 'true',
    seatsLeft: offerDetail(offer, 'seatsLeft'),
  }];
  return list.map((o) => ({
    ref: o.offerReference,
    type: o.fareIdentifier || '',
    name: fareLabel(o.fareIdentifier),
    price: Number(o.price),
    refund: o.refundType || '',
    cabinBag: o.cabinBaggage || '',
    checkinBag: o.checkinBaggage || '',
    meal: Boolean(o.mealIncluded),
    seatsLeft: Number(o.seatsLeft) > 0 ? Number(o.seatsLeft) : null,
    bookingClass: o.bookingClass || '',
  })).sort((a, b) => a.price - b.price);
}

/**
 * One direction of a live offer. With prefix 'return' it reads the return leg of a combined
 * (international round-trip) offer — returnDepartureTime, returnAirline… — or null if there is none.
 */
function liveLeg(offer, prefix, fromCity, toCity) {
  const key = (name) => (prefix ? `${prefix}${name[0].toUpperCase()}${name.slice(1)}` : name);
  const departure = offerDetail(offer, key('departureTime'));
  if (prefix && !departure) return null;
  const arrival = offerDetail(offer, key('arrivalTime'));
  const dayShift = departure && arrival
    ? Math.round((new Date(arrival.slice(0, 10)) - new Date(departure.slice(0, 10))) / 864e5)
    : 0;
  const code = offerDetail(offer, key('airlineCode'));
  const duration = offerDetail(offer, key('duration'));
  return {
    airline: airlineFor(code, offerDetail(offer, key('airline')) || (prefix ? '' : offer.name)),
    flightNo: `${code}-${offerDetail(offer, key('flightNumber'))}`,
    fromCode: offerDetail(offer, key('origin')) || placeOf(fromCity).code,
    toCode: offerDetail(offer, key('destination')) || placeOf(toCity).code,
    fromCity,
    toCity,
    depDate: departure ? departure.slice(0, 10) : '',
    dep: clockTime(departure),
    arr: clockTime(arrival),
    dayShift: Math.max(0, dayShift),
    duration: formatDuration(durationMins(duration)),
    durationMins: durationMins(duration),
    stops: Number(offerDetail(offer, key('stops')) || 0),
    via: parseJson(offerDetail(offer, key('layovers')), []).map((l) => ({
      airport: l.airport, city: l.city || l.airport, layover: l.minutes ? formatDuration(l.minutes) : '',
    })),
  };
}

/** Maps a live supplier offer onto the shape the list renders. */
function fromLiveOffer(offer, from, to) {
  // Domestic round trips arrive as separate 'Onward' and 'Return' offers; a 'Return' offer flies to → from.
  const leg = offerDetail(offer, 'leg');
  const isReturnLeg = leg === 'Return';
  const seats = Number(offerDetail(offer, 'seatsLeft'));
  return {
    id: `${offer.supplierCode}-${offer.deduplicationKey}-${offerDetail(offer, 'offerReference')}`,
    offer,
    leg,
    ...liveLeg(offer, '', isReturnLeg ? to : from, isReturnLeg ? from : to),
    ret: liveLeg(offer, 'return', to, from),
    price: Number(offer.totalPrice),
    refund: offerDetail(offer, 'refundType'),
    cabinBag: offerDetail(offer, 'cabinBaggage'),
    checkinBag: offerDetail(offer, 'checkinBaggage'),
    meal: offerDetail(offer, 'mealIncluded') === 'true',
    seatsLeft: Number.isFinite(seats) && seats > 0 ? seats : null,
    fareName: fareLabel(offerDetail(offer, 'fareIdentifier')),
    fares: faresFor(offer),
  };
}

/** Maps a sample flight (used when no live API is available) onto the same shape. */
function fromSample(f) {
  return {
    id: f.id,
    sample: f,
    airline: f.airline,
    flightNo: f.flightNo,
    fromCode: placeOf(f.from).code,
    toCode: placeOf(f.to).code,
    fromCity: f.from,
    toCity: f.to,
    dep: f.dep,
    arr: f.arr,
    dayShift: f.overnight ? 1 : 0,
    duration: f.duration,
    durationMins: durationMins(f.duration),
    stops: f.stops,
    via: f.segments.slice(1).map((s) => ({ airport: placeOf(s.from).code || s.from, city: s.from, layover: s.layover })),
    price: f.price,
    refund: f.refundable ? 'Partially refundable' : 'Non-refundable',
    cabinBag: '7 kg',
    checkinBag: '15 kg',
    meal: false,
    seatsLeft: null,
    fareName: f.aircraft,
    fares: [],
  };
}

function SortCards({ sort, setSort, flights }) {
  const pick = (cmp) => (flights.length ? [...flights].sort(cmp)[0] : null);
  const hint = {
    cheap: pick((a, b) => a.price - b.price),
    fast: pick((a, b) => a.durationMins - b.durationMins),
    early: pick((a, b) => toMins(a.dep) - toMins(b.dep)),
    late: pick((a, b) => toMins(b.dep) - toMins(a.dep)),
  };
  const sub = {
    cheap: (f) => formatPrice(f.price),
    fast: (f) => `${f.duration} · ${formatPrice(f.price)}`,
    early: (f) => `${f.dep} · ${formatPrice(f.price)}`,
    late: (f) => `${f.dep} · ${formatPrice(f.price)}`,
  };
  return (
    <div className="fl-sort" role="tablist" aria-label="Sort flights">
      {sortOptions.map(([k, l]) => (
        <button key={k} role="tab" aria-selected={sort === k} className={`fl-sort-card ${sort === k ? 'active' : ''}`} onClick={() => setSort(k)}>
          <b>{l}</b>
          <span>{hint[k] ? sub[k](hint[k]) : '—'}</span>
        </button>
      ))}
    </div>
  );
}

function SlotPicker({ value, onChange }) {
  return (
    <div className="slot-grid">
      {timeSlots.map(([k, name, range]) => (
        <button key={k} type="button" aria-pressed={value.includes(k)} className={`slot fl-slot ${value.includes(k) ? 'active' : ''}`}
          onClick={() => onChange(toggleIn(value, k, !value.includes(k)))}>
          <b>{name}</b><span>{range}</span>
        </button>
      ))}
    </div>
  );
}

function stopLabel(f) {
  if (!f.stops) return 'Non-stop';
  const via = f.via.map((v) => v.airport).filter(Boolean).join(', ');
  return `${f.stops} stop${f.stops > 1 ? 's' : ''}${via ? ` via ${via}` : ''}`;
}

function FareOptions({ fares, onSelectFare }) {
  return (
    <div className="fl-fares" role="list" aria-label="Fare options">
      {fares.map((fare, i) => (
        <div key={fare.ref} className={`fl-fare ${i === 0 ? 'lowest' : ''}`} role="listitem">
          <div className="fl-fare-head">
            <span>
              <b>{fare.name}</b>
              {fare.bookingClass && <span className="muted small"> · Class {fare.bookingClass}</span>}
            </span>
            {i === 0 && fares.length > 1 && <span className="tag">Lowest</span>}
          </div>
          <b className="price">{formatPrice(fare.price)}</b>
          <ul>
            <li><Icon name="bag" size={13} /> {[fare.cabinBag && `Cabin ${fare.cabinBag}`, fare.checkinBag && `Check-in ${fare.checkinBag}`].filter(Boolean).join(' · ') || 'Baggage as per airline'}</li>
            <li className={fare.refund === 'Non-refundable' ? 'text-coral' : ''}><Icon name="file" size={13} /> {fare.refund || 'Refund as per airline rules'}</li>
            <li><Icon name="check" size={13} /> {fare.meal ? 'Free meal included' : 'Meals at extra cost'}</li>
            {fare.seatsLeft && fare.seatsLeft <= 5 && <li className="text-coral">Only {fare.seatsLeft} seat{fare.seatsLeft > 1 ? 's' : ''} left</li>}
          </ul>
          <button type="button" className={`btn ${i === 0 ? 'btn-primary' : 'btn-outline'}`} onClick={() => onSelectFare(fare)}>Select</button>
        </div>
      ))}
    </div>
  );
}

/** Airline, departure, path and arrival cells for one direction of a flight. */
function LegCells({ leg, label }) {
  return (
    <>
      <div className="fl-airline">
        <AirlineLogo airline={leg.airline} size={40} />
        <div>
          {label && <span className="fl-leg-label">{label}</span>}
          <b>{leg.airline.name}</b>
          <span className="muted small">{leg.flightNo}</span>
        </div>
      </div>
      <div className="fl-point">
        <b className="fl-time">{leg.dep}</b>
        <span className="fl-code">{leg.fromCode}</span>
        <span className="muted small">{leg.fromCity}</span>
        {leg.depDate && <span className="muted small fl-date">{fmtDay(leg.depDate)}</span>}
      </div>
      <div className="fl-path">
        <span className="small muted">{leg.duration}</span>
        <span className="fl-line">
          {leg.via.map((v) => <i key={v.airport} className="fl-stop-dot" title={`${v.city}${v.layover ? ` · ${v.layover} layover` : ''}`} />)}
          <span className="fl-plane"><Icon name="flights" size={13} /></span>
        </span>
        <span className={`small fl-stops ${leg.stops ? 'text-coral' : 'text-primary'}`}>{stopLabel(leg)}</span>
      </div>
      <div className="fl-point">
        <b className="fl-time">{leg.arr}{leg.dayShift > 0 && <sup className="text-coral"> +{leg.dayShift}</sup>}</b>
        <span className="fl-code">{leg.toCode}</span>
        <span className="muted small">{leg.toCity}</span>
      </div>
    </>
  );
}

function LegDetails({ leg, label }) {
  return (
    <>
      <div><span className="muted small">{label}Departure</span><b>{leg.depDate ? `${fmtDay(leg.depDate)}, ` : ''}{leg.dep} · {leg.fromCode}</b></div>
      <div><span className="muted small">{label}Arrival</span><b>{leg.arr}{leg.dayShift > 0 ? ` (+${leg.dayShift} day)` : ''} · {leg.toCode}</b></div>
      <div>
        <span className="muted small">{label}Layovers</span>
        <b>{leg.via.length ? leg.via.map((v) => `${v.city}${v.layover ? ` (${v.layover})` : ''}`).join(', ') : 'None'}</b>
      </div>
    </>
  );
}

function FlightCard({ f, priceNote, cta, onSelect, onSelectFare }) {
  const [panel, setPanel] = useState(null);
  const toggle = (name) => setPanel(panel === name ? null : name);
  const manyFares = f.fares.length > 1;
  return (
    <article className="fl-card">
      <div className={`fl-main ${f.ret ? 'fl-main-rt' : ''}`}>
        {f.ret ? (
          <div className="fl-legs">
            <div className="fl-leg"><LegCells leg={f} label="Onward" /></div>
            <div className="fl-leg"><LegCells leg={f.ret} label="Return" /></div>
          </div>
        ) : <LegCells leg={f} />}
        <div className="fl-price">
          {manyFares && <span className="muted small">from</span>}
          <b className="price big">{formatPrice(f.price)}</b>
          <span className="muted small">{priceNote}</span>
          {manyFares
            ? <button className="btn btn-primary" aria-expanded={panel === 'fares'} onClick={() => toggle('fares')}>{panel === 'fares' ? 'Hide fares' : 'View fares'}</button>
            : <button className="btn btn-primary" onClick={onSelect}>{cta}</button>}
        </div>
      </div>
      <div className="fl-foot">
        <div className="fl-tags">
          {manyFares
            ? <span className="fl-meta"><b>{f.fares.length} fare options</b>&nbsp;· {[...new Set(f.fares.map((x) => x.name))].join(', ')}</span>
            : <>
              {f.fares[0] && <span className="tag fl-fare-tag">{f.fares[0].name}</span>}
              {f.refund && <span className={`tag ${f.refund === 'Non-refundable' ? 'tag-coral' : ''}`}>{f.refund}</span>}
              {(f.cabinBag || f.checkinBag) && (
                <span className="fl-meta"><Icon name="bag" size={13} /> {[f.cabinBag && `Cabin ${f.cabinBag}`, f.checkinBag && `Check-in ${f.checkinBag}`].filter(Boolean).join(' · ')}</span>
              )}
              {f.meal && <span className="fl-meta"><Icon name="check" size={13} /> Free meal</span>}
            </>}
          {f.seatsLeft && f.seatsLeft <= 5 && !manyFares && <span className="fl-meta text-coral">Only {f.seatsLeft} seat{f.seatsLeft > 1 ? 's' : ''} left</span>}
        </div>
        <button type="button" className="link small" aria-expanded={panel === 'details'} onClick={() => toggle('details')}>
          Flight details <Icon name="down" size={13} />
        </button>
      </div>
      {panel === 'fares' && <FareOptions fares={f.fares} onSelectFare={onSelectFare} />}
      {panel === 'details' && (
        <div className="flight-details">
          <LegDetails leg={f} label={f.ret ? 'Onward ' : ''} />
          {f.ret && <LegDetails leg={f.ret} label="Return " />}
          <div><span className="muted small">Fare</span><b>{f.fareName || 'Standard'}</b></div>
        </div>
      )}
    </article>
  );
}

/** The chosen flight and fare in one round-trip column; falls back to the top of the list. */
function resolvePick(flights, pick) {
  const flight = (pick && flights.find((f) => f.id === pick.id)) || flights[0];
  if (!flight) return null;
  const fare = (pick && flight.fares.find((x) => x.ref === pick.fareRef)) || flight.fares[0];
  return { flight, fare };
}

/** One column of a domestic round trip: pick a flight (and fare) for this direction. */
function LegPicker({ title, subtitle, flights, selected, onPick }) {
  return (
    <section className="fl-rt-col" aria-label={title}>
      <header className="fl-rt-head">
        <b>{title}</b>
        <span className="muted small">{subtitle} · {flights.length} flight{flights.length === 1 ? '' : 's'}</span>
      </header>
      <div className="fl-rt-list" role="radiogroup" aria-label={title}>
        {flights.map((f) => {
          const active = selected?.flight.id === f.id;
          return (
            <div key={f.id} className={`fl-rt-row ${active ? 'active' : ''}`}>
              <button type="button" role="radio" aria-checked={active} className="fl-rt-pick"
                onClick={() => onPick({ id: f.id, fareRef: f.fares[0]?.ref })}>
                <span className="fl-rt-air">
                  <AirlineLogo airline={f.airline} size={28} />
                  <span><b>{f.airline.name}</b><span className="muted small">{f.flightNo}</span></span>
                </span>
                <span className="fl-rt-times">
                  <b>{f.dep}</b>
                  <span className="muted small">{f.duration}<br />{stopLabel(f)}</span>
                  <b>{f.arr}{f.dayShift > 0 && <sup className="text-coral"> +{f.dayShift}</sup>}</b>
                </span>
                <b className="price">{formatPrice(active ? selected.fare.price : f.price)}</b>
              </button>
              {active && f.fares.length > 1 && (
                <div className="fl-rt-fares" aria-label="Fare options">
                  {f.fares.map((fare) => (
                    <button key={fare.ref} type="button" aria-pressed={fare.ref === selected.fare.ref}
                      className={`chip ${fare.ref === selected.fare.ref ? 'active' : ''}`}
                      onClick={() => onPick({ id: f.id, fareRef: fare.ref })}>
                      {fare.name} · {formatPrice(fare.price)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {!flights.length && <p className="muted small fl-rt-empty">No flights match these filters.</p>}
      </div>
    </section>
  );
}

function RoundTripBar({ onward, back, priceNote, onContinue }) {
  const leg = (label, { flight }) => (
    <div className="fl-rt-bar-leg">
      <span className="muted small">{label} · {flight.airline.name}</span>
      <b>{flight.dep} → {flight.arr}{flight.dayShift > 0 ? ` +${flight.dayShift}` : ''}</b>
    </div>
  );
  return (
    <div className="fl-rt-bar" role="region" aria-label="Selected flights">
      {leg('Onward', onward)}
      {leg('Return', back)}
      <div className="fl-rt-bar-total">
        <b className="price big">{formatPrice(onward.fare.price + back.fare.price)}</b>
        <span className="muted small">{priceNote}</span>
      </div>
      <button type="button" className="btn btn-primary" onClick={onContinue}>Continue</button>
    </div>
  );
}

function SkeletonCards() {
  return [0, 1, 2, 3].map((i) => (
    <div key={i} className="fl-card fl-skeleton" aria-hidden="true">
      <div className="fl-main">
        <span className="sk sk-logo" /><span className="sk" /><span className="sk sk-wide" /><span className="sk" /><span className="sk sk-btn" />
      </div>
    </div>
  ));
}

export default function FlightList() {
  const q = useQuery();
  const { search } = useLocation();
  const navigate = useNavigate();
  const from = q.from || 'Delhi';
  const to = q.to || 'Mumbai';
  const date = q.date || addDaysIso(7);
  const paxCounts = {
    adults: Number(q.adults || 1),
    children: Number(q.children || 0),
    infants: Number(q.infants || 0),
  };
  const pax = Number(q.travellers || q.adults || 1);
  const liveSearch = useSupplierSearch('Flight', {
    origin: placeOf(from).code || from,
    destination: placeOf(to).code || to,
    departureDate: date,
    returnDate: q.returnDate || '',
    adults: q.adults || '1',
    children: q.children || '0',
    infants: q.infants || '0',
    cabin: q.cabin || 'Economy',
    tripType: q.trip || 'oneway',
  }, LIVE);

  const all = useMemo(
    () => (LIVE ? liveSearch.offers.map((o) => fromLiveOffer(o, from, to)) : generateFlights(from, to).map(fromSample)),
    [liveSearch.offers, from, to],
  );

  const [sort, setSort] = useState('cheap');
  const [stops, setStops] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [depSlots, setDepSlots] = useState([]);
  const [arrSlots, setArrSlots] = useState([]);
  const [layoverAirports, setLayoverAirports] = useState([]);
  const [fareTypes, setFareTypes] = useState([]);
  const [refundOnly, setRefundOnly] = useState(false);
  const [mealOnly, setMealOnly] = useState(false);
  const [maxPrice, setMaxPrice] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [selectError, setSelectError] = useState('');
  const [onwardPick, setOnwardPick] = useState(null);
  const [returnPick, setReturnPick] = useState(null);

  const priceBounds = useMemo(() => {
    if (!all.length) return [0, 0];
    const prices = all.map((f) => f.price);
    return [Math.floor(Math.min(...prices)), Math.ceil(Math.max(...prices))];
  }, [all]);

  const facets = useMemo(() => {
    const minBy = (items, key) => items.reduce((acc, f) => {
      const k = key(f);
      acc[k] = Math.min(acc[k] ?? Infinity, f.price);
      return acc;
    }, {});
    const carrierMin = minBy(all, (f) => f.airline.code);
    const carrierList = Object.values(all.reduce((acc, f) => {
      acc[f.airline.code] = acc[f.airline.code] || { airline: f.airline, count: 0 };
      acc[f.airline.code].count += 1;
      return acc;
    }, {})).sort((a, b) => carrierMin[a.airline.code] - carrierMin[b.airline.code]);
    const viaList = Object.values(all.flatMap((f) => f.via).reduce((acc, v) => {
      if (v.airport) acc[v.airport] = acc[v.airport] || v;
      return acc;
    }, {}));
    return {
      stops: minBy(all, (f) => stopBucket(f.stops)),
      carriers: carrierList,
      carrierMin,
      via: viaList,
      hasRefund: all.some((f) => (f.refund && f.refund !== 'Non-refundable') ||
        f.fares.some((x) => x.refund && x.refund !== 'Non-refundable')),
      hasMeal: all.some((f) => f.meal || f.fares.some((x) => x.meal)),
      fareTypes: Object.values(all.flatMap((f) => f.fares).reduce((acc, fare) => {
        const entry = acc[fare.name] || { name: fare.name, count: 0, min: Infinity };
        entry.count += 1;
        entry.min = Math.min(entry.min, fare.price);
        acc[fare.name] = entry;
        return acc;
      }, {})).sort((a, b) => a.min - b.min),
    };
  }, [all]);

  const activeCount = stops.length + carriers.length + depSlots.length + arrSlots.length +
    layoverAirports.length + fareTypes.length + (refundOnly ? 1 : 0) + (mealOnly ? 1 : 0) + (maxPrice != null ? 1 : 0);

  const clearFilters = () => {
    setStops([]); setCarriers([]); setDepSlots([]); setArrSlots([]); setLayoverAirports([]);
    setFareTypes([]); setRefundOnly(false); setMealOnly(false); setMaxPrice(null);
  };

  const list = useMemo(() => {
    // Fare-level filters keep only the matching fares; the card then shows the cheapest of them.
    const fareMatches = (fare) =>
      (!fareTypes.length || fareTypes.includes(fare.name)) &&
      (!refundOnly || (fare.refund && fare.refund !== 'Non-refundable')) &&
      (!mealOnly || fare.meal) &&
      (maxPrice == null || fare.price <= maxPrice);
    const narrowed = all.map((f) => {
      if (!f.fares.length) return f;
      const fares = f.fares.filter(fareMatches);
      if (!fares.length) return null;
      const [best] = fares;
      return {
        ...f, fares, price: best.price, refund: best.refund, cabinBag: best.cabinBag,
        checkinBag: best.checkinBag, meal: best.meal, seatsLeft: best.seatsLeft, fareName: best.name,
      };
    }).filter(Boolean);
    const r = narrowed.filter((f) =>
      (!stops.length || stops.includes(stopBucket(f.stops))) &&
      (!carriers.length || carriers.includes(f.airline.code)) &&
      (!depSlots.length || depSlots.includes(slotOf(toMins(f.dep)))) &&
      (!arrSlots.length || arrSlots.includes(slotOf(toMins(f.arr)))) &&
      (!layoverAirports.length || (f.stops > 0 && f.via.every((v) => layoverAirports.includes(v.airport)))) &&
      (!refundOnly || (f.refund && f.refund !== 'Non-refundable')) &&
      (!mealOnly || f.meal) &&
      (maxPrice == null || f.price <= maxPrice));
    const cmp = {
      cheap: (a, b) => a.price - b.price || a.durationMins - b.durationMins,
      fast: (a, b) => a.durationMins - b.durationMins || a.price - b.price,
      early: (a, b) => toMins(a.dep) - toMins(b.dep) || a.price - b.price,
      late: (a, b) => toMins(b.dep) - toMins(a.dep) || a.price - b.price,
    }[sort];
    return [...r].sort(cmp);
  }, [all, stops, carriers, depSlots, arrSlots, layoverAirports, fareTypes, refundOnly, mealOnly, maxPrice, sort]);

  const paxTotal = paxCounts.adults + paxCounts.children + paxCounts.infants;
  const priceNote = LIVE
    ? (paxTotal > 1 ? `total for ${paxTotal} travellers` : 'per adult')
    : 'per adult';

  // Domestic round trips: one list per direction, and the traveller picks a flight from each.
  const splitMode = LIVE && all.some((f) => f.leg === 'Return');
  const onwardList = splitMode ? list.filter((f) => f.leg !== 'Return') : list;
  const returnList = splitMode ? list.filter((f) => f.leg === 'Return') : [];
  const onwardSel = splitMode ? resolvePick(onwardList, onwardPick) : null;
  const returnSel = splitMode ? resolvePick(returnList, returnPick) : null;

  // Live fares open in a new tab; the details page reviews the fare with the supplier.
  const openSelection = (flight, allFares) => {
    setSelectError('');
    const id = saveSelection({ flight, allFares, query: q, search });
    if (!id) {
      setSelectError('Your browser blocked storage needed to open this flight. Allow site data and try again.');
      return;
    }
    const tab = window.open(`/flights/live/${id}${search}`, '_blank');
    if (!tab) setSelectError('Your browser blocked the new tab. Allow pop-ups for this site and select the flight again.');
  };

  const openLiveDetails = (f, fare = f.fares[0]) => {
    // The details page reviews exactly the fare family the traveller picked, and can offer
    // the flight's other fares (unfiltered) if that one has no seats, meals or baggage.
    openSelection(withFare(f, fare), all.find((x) => x.id === f.id)?.fares || f.fares);
  };

  // Both price ids are reviewed and booked together, as the supplier requires for a domestic return.
  const openRoundTrip = () => {
    const out = withFare(onwardSel.flight, onwardSel.fare);
    const back = withFare(returnSel.flight, returnSel.fare);
    const price = out.price + back.price;
    openSelection({
      ...out,
      id: `${out.id}~${back.id}`,
      ret: back,
      price,
      fareName: out.fareName === back.fareName ? out.fareName : `${out.fareName} + ${back.fareName}`,
      offer: {
        ...out.offer,
        totalPrice: price,
        details: {
          ...out.offer.details,
          offerReference: JSON.stringify([out.offer.details.offerReference, back.offer.details.offerReference]),
        },
      },
    }, []);
  };

  const loading = LIVE && liveSearch.loading;
  const failed = LIVE && liveSearch.error;

  return (
    <>
      <PageHero tab="flights" title={`Flights from ${from} to ${to}`}
        subtitle={`${placeOf(from).code} → ${placeOf(to).code} · ${pax} traveller${pax > 1 ? 's' : ''} · ${q.cabin || 'Economy'}`} query={q}
        summary={{
          tag: q.returnDate ? 'Round Trip' : 'One Way',
          items: [
            ['From', `${from}${placeOf(from).code ? ` (${placeOf(from).code})` : ''}`],
            ['To', `${to}${placeOf(to).code ? ` (${placeOf(to).code})` : ''}`],
            ['Departure', fmtDay(date)],
            ...(q.returnDate ? [['Return', fmtDay(q.returnDate)]] : []),
            ['Travellers & Class', `${pax} Traveller${pax > 1 ? 's' : ''}, ${q.cabin || 'Economy'}`],
          ],
        }} />
      <div className="container listing fl-listing">
        <button type="button" className="btn fl-filter-toggle" aria-expanded={showFilters} onClick={() => setShowFilters(!showFilters)}>
          <Icon name="menu" size={16} /> Filters{activeCount ? ` (${activeCount})` : ''}
        </button>
        <aside className={`filters fl-filters ${showFilters ? 'open' : ''}`} aria-label="Flight filters">
          <div className="filters-head"><b>Filters</b>
            {activeCount > 0 && <button className="link small" onClick={clearFilters}>Clear all</button>}
          </div>
          <FilterGroup title="Stops">
            <div className="fl-stop-grid">
              {stopOptions.filter(([k]) => facets.stops[k] != null || !all.length).map(([k, l]) => (
                <button key={k} type="button" aria-pressed={stops.includes(k)} className={`slot fl-slot ${stops.includes(k) ? 'active' : ''}`}
                  onClick={() => setStops(toggleIn(stops, k, !stops.includes(k)))}>
                  <b>{l}</b><span>{facets.stops[k] != null ? formatPrice(facets.stops[k]) : '—'}</span>
                </button>
              ))}
            </div>
          </FilterGroup>
          {facets.carriers.length > 0 && (
            <FilterGroup title="Airlines">
              {facets.carriers.map(({ airline, count }) => (
                <Check key={airline.code}
                  label={<span className="fl-check-airline"><AirlineLogo airline={airline} size={20} />{airline.name} <span className="muted small">({count})</span></span>}
                  checked={carriers.includes(airline.code)}
                  onChange={(on) => setCarriers(toggleIn(carriers, airline.code, on))}
                  extra={formatPrice(facets.carrierMin[airline.code])} />
              ))}
            </FilterGroup>
          )}
          {priceBounds[1] > priceBounds[0] && (
            <FilterGroup title="Price">
              <input className="fl-range" type="range" aria-label="Maximum price"
                min={priceBounds[0]} max={priceBounds[1]} step={1}
                value={maxPrice ?? priceBounds[1]}
                onChange={(e) => { const v = Number(e.target.value); setMaxPrice(v >= priceBounds[1] ? null : v); }} />
              <div className="fl-range-labels">
                <span className="muted small">{formatPrice(priceBounds[0])}</span>
                <b className="small">Up to {formatPrice(maxPrice ?? priceBounds[1])}</b>
              </div>
            </FilterGroup>
          )}
          <FilterGroup title={splitMode ? 'Departure time' : `Departure from ${from}`}>
            <SlotPicker value={depSlots} onChange={setDepSlots} />
          </FilterGroup>
          <FilterGroup title={splitMode ? 'Arrival time' : `Arrival at ${to}`}>
            <SlotPicker value={arrSlots} onChange={setArrSlots} />
          </FilterGroup>
          {facets.via.length > 0 && (
            <FilterGroup title="Layover airports">
              {facets.via.map((v) => (
                <Check key={v.airport} label={`${v.city} (${v.airport})`} checked={layoverAirports.includes(v.airport)}
                  onChange={(on) => setLayoverAirports(toggleIn(layoverAirports, v.airport, on))} />
              ))}
            </FilterGroup>
          )}
          {facets.fareTypes.length > 1 && (
            <FilterGroup title="Fare types">
              {facets.fareTypes.map((t) => (
                <Check key={t.name} label={<>{t.name} <span className="muted small">({t.count})</span></>}
                  checked={fareTypes.includes(t.name)}
                  onChange={(on) => setFareTypes(toggleIn(fareTypes, t.name, on))}
                  extra={formatPrice(t.min)} />
              ))}
            </FilterGroup>
          )}
          {(facets.hasRefund || facets.hasMeal) && (
            <FilterGroup title="Fare features">
              {facets.hasRefund && <Check label="Refundable fares" checked={refundOnly} onChange={setRefundOnly} />}
              {facets.hasMeal && <Check label="Free meal included" checked={mealOnly} onChange={setMealOnly} />}
            </FilterGroup>
          )}
        </aside>

        <div className="results">
          {failed && (
            <div className="api-message error" role="alert">
              <b>Live search is unavailable.</b> {liveSearch.error.message}
            </div>
          )}
          {LIVE && liveSearch.suppliersWithErrors.length > 0 && (
            <div className="api-message warning" role="status">
              Some suppliers could not be reached: {liveSearch.suppliersWithErrors.join(', ')}. Prices shown are from the suppliers that responded.
            </div>
          )}
          {selectError && <div className="api-message error" role="alert">{selectError}</div>}
          {!failed && (
            <>
              <SortCards sort={sort} setSort={setSort} flights={onwardList} />
              <div className="fl-count" role="status">
                {loading && 'Searching live fares…'}
                {!loading && splitMode && <>Showing <b>{onwardList.length}</b> onward and <b>{returnList.length}</b> return flights</>}
                {!loading && !splitMode && <>Showing <b>{list.length}</b> of {all.length} flights</>}
                {activeCount > 0 && <button className="link small" onClick={clearFilters}>Clear filters</button>}
              </div>
            </>
          )}
          {loading && <SkeletonCards />}
          {!loading && !failed && !all.length && <Empty text="No flights were found for this route and date. Try another date." />}
          {!loading && all.length > 0 && !list.length && <Empty />}
          {splitMode && !loading && list.length > 0 && (
            <>
              <div className="fl-rt">
                <LegPicker title={`${from} → ${to}`} subtitle={fmtDay(date)} flights={onwardList}
                  selected={onwardSel} onPick={setOnwardPick} />
                <LegPicker title={`${to} → ${from}`} subtitle={fmtDay(q.returnDate)} flights={returnList}
                  selected={returnSel} onPick={setReturnPick} />
              </div>
              {onwardSel && returnSel && (
                <RoundTripBar onward={onwardSel} back={returnSel} priceNote={priceNote} onContinue={openRoundTrip} />
              )}
            </>
          )}
          {!splitMode && list.map((f) => (
            <FlightCard key={f.id} f={f} priceNote={priceNote}
              cta={f.sample ? 'View Fares' : 'Select'}
              onSelect={() => (f.sample ? navigate(`/flights/${f.id}${search}`) : openLiveDetails(f))}
              onSelectFare={(fare) => openLiveDetails(f, fare)} />
          ))}
        </div>
      </div>
    </>
  );
}
